/**
 * 인천 지도 — 7개 시군구 SVG를 318×281 캔버스에 절대 좌표로 합성.
 *
 * 디자인 의도:
 *  - 시군구별로 SVG 분리 (디자이너 export). 각 path는 동 단위 가용성까지 색으로 표현.
 *  - 중구는 영종도 + 본토 path 2개로 분리되어 라벨도 두 곳. 클릭 시 같은 카드로 스크롤.
 *  - 인천은 subRegionList 비어있어 필터 분기 없음. props.subRegion 무시.
 *
 * 반응형/key 전략은 GyeonggiMap과 동일.
 * 데이터/좌표는 `constants/incheonRegions.ts` 참조.
 */

import { Fragment, useMemo } from 'react'

import {
  INCHEON_CANVAS,
  INCHEON_CODE_TO_SIGUNGU_NAME,
  INCHEON_REGIONS,
} from '../../../constants/incheonRegions'

import { MapStage, RegionImage, RegionLabel, RegionLabelHitArea, Wrapper } from './GyeonggiMap.styles'

interface IncheonMapProps {
  /** 인천은 subRegion 분기 없음 — RegionMapComponentProps signature 호환 위해 받지만 사용 X */
  subRegion?: string | null
  /** 시 라벨 클릭 시 호출. 인자는 백엔드 SiGunGu.name(한글, 예: "남동구") */
  onLabelClick?: (sigunguName: string) => void
}

export default function IncheonMap({ onLabelClick }: IncheonMapProps) {
  const canvas = INCHEON_CANVAS

  /** px 좌표 → 캔버스 기준 % 문자열 */
  const toPercent = useMemo(
    () => ({
      x: (px: number) => `${(px / canvas.width) * 100}%`,
      y: (px: number) => `${(px / canvas.height) * 100}%`,
    }),
    [canvas]
  )

  const handleLabelClick = (regionCode: string) => {
    const sigunguName = INCHEON_CODE_TO_SIGUNGU_NAME[regionCode]
    if (!sigunguName) {
      if (import.meta.env.DEV) {
        console.warn(`[IncheonMap] 매핑 없는 region code: "${regionCode}"`)
      }
      return
    }
    onLabelClick?.(sigunguName)
  }

  return (
    <Wrapper>
      <MapStage
        style={{
          maxWidth: canvas.width,
          aspectRatio: `${canvas.width} / ${canvas.height}`,
        }}>
        {/*
         * region 단위로 [라벨 button, path img] sibling 배치.
         * styles의 button:active + img selector가 이 순서를 의존 — 순서 바꾸지 말 것.
         * label 없는 path(예: empty.svg)는 button 생략하고 img만 렌더 — sibling selector 매칭 자연 회피.
         * 중구는 path 두 개라 같은 code 가짐 — key는 idx 기반으로 unique 보장.
         */}
        {INCHEON_REGIONS.map((region, idx) => (
          <Fragment key={`region-${idx}`}>
            {region.label && (
              <RegionLabelHitArea
                type='button'
                aria-label={`${region.name} 영역 보기`}
                onClick={() => handleLabelClick(region.code)}
                style={{
                  left: toPercent.x(region.label.x),
                  top: toPercent.y(region.label.y),
                }}>
                <RegionLabel>{region.name}</RegionLabel>
              </RegionLabelHitArea>
            )}
            <RegionImage
              src={region.svgUrl}
              alt=''
              style={{
                left: toPercent.x(region.x),
                top: toPercent.y(region.y),
                width: toPercent.x(region.width),
              }}
            />
          </Fragment>
        ))}
      </MapStage>
    </Wrapper>
  )
}
